import { Flex, TextV2 } from '@withjoy/joykit';
import React from 'react';
import { Redirect } from '@react-router';
import { FormContainer, FormHeader } from '../components/FormContainer';
import { useAuth } from '@shared/components/AuthProvider';
import { useTranslation } from '@shared/core';
import { EventInfoData, useEventInfo } from '@shared/utils/eventInfo';
import { Accept } from './components/Accept';
import { LogIn } from './components/LogIn';
import { useGetInviteById } from './hooks/useGetInviteById';
import { useEventPhoto } from '@shared/utils/eventPhoto/useEventPhoto';
import { BackButton } from './components/BackButton';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { CollaborationInviteState } from '@graphql/generated';

type FormHeaderWithImageProps = {
  eventDisplayName: string;
  eventPhotoUrl?: string;
  subheading?: string;
  eventInfo: EventInfoData;
};

const FormHeaderWithImage = (props: FormHeaderWithImageProps) => {
  const { eventDisplayName, eventPhotoUrl, eventInfo, subheading } = props;

  return (
    <FormHeader gap="0.75rem">
      {eventPhotoUrl && (
        <img
          src={eventPhotoUrl}
          alt={eventDisplayName}
          style={{ borderRadius: '10px', width: '70%', margin: '0 auto 2rem auto' }}
        />
      )}
      {eventInfo.eventDisplayName && <TextV2 typographyVariant="hed6">{eventInfo.eventDisplayName}</TextV2>}
      {subheading && <TextV2 typographyVariant="hed2">{subheading}</TextV2>}
    </FormHeader>
  );
};

type AcceptInviteProps = {
  eventHandle: string;
  inviteUUID: string;
};

export const AcceptInvite = (props: AcceptInviteProps) => {
  // Check if logged in
  // If yes, display accept button
  // If no, display login button

  const { eventHandle, inviteUUID } = props;
  const { eventInfo, loading: infoLoading } = useEventInfo();
  const { isLoggedIn, currentUser, isCurrentUserSettled } = useAuth();
  const { data: inviteData, error: inviteError, loading: inviteLoading } = useGetInviteById(inviteUUID);
  const { tq } = useTranslation('collaboration');
  const { eventPhoto } = useEventPhoto(eventHandle);

  const loading = infoLoading || !eventInfo || !isCurrentUserSettled || inviteLoading;
  const error = !inviteData || inviteError;

  if (loading) {
    return <JoyLogoLoader loaderKey="acceptCollaborationInvite" />;
  }

  if (error || inviteData.state !== CollaborationInviteState.pending) {
    return (
      <Flex
        width="100vw"
        minHeight="100vh"
        justifyContent="center"
        alignItems="center"
      >
        <FormContainer
          justifyContent="center"
          alignItems="center"
        >
          <FormHeaderWithImage
            eventDisplayName={eventInfo?.eventDisplayName ?? eventHandle}
            eventPhotoUrl={eventPhoto?.url}
            eventInfo={eventInfo}
            subheading={tq`This invite is invalid or has expired.`}
          />
          <TextV2>{tq`Please contact the event organizer for a new invitation.`}</TextV2>
          <BackButton eventHandle={eventHandle} />
        </FormContainer>
      </Flex>
    );
  }

  // sanity check
  // Bounce if this is an invite for a different event
  if (inviteData.eventId !== eventInfo?.eventId) {
    return <Redirect to={`/${eventHandle}`} />;
  }

  return (
    <Flex
      width="100vw"
      minHeight="100vh"
      justifyContent="center"
      alignItems="center"
    >
      <FormContainer>
        <FormHeaderWithImage
          eventDisplayName={eventInfo?.eventDisplayName ?? eventHandle}
          eventPhotoUrl={eventPhoto?.url}
          eventInfo={eventInfo}
          subheading={tq`You've been invited to become a collaborator.`}
        />
        {isLoggedIn ? (
          <Accept
            inviteData={inviteData}
            eventHandle={eventHandle}
            eventDisplayName={eventInfo?.eventDisplayName ?? eventHandle}
            currentUser={currentUser}
          />
        ) : (
          <LogIn eventHandle={eventHandle} />
        )}
      </FormContainer>
    </Flex>
  );
};
